import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, UserCog, Briefcase, Receipt, ClipboardList,
  BarChart3, Settings2, Building2, ShieldCheck, LogOut, UserSquare2, Wallet, Store, FileBarChart2,
  ClipboardCheck, PlayCircle, Clock3, Inbox, PieChart, Users2, Landmark, CalendarCheck, Bell, CalendarClock,
  NotebookPen, MessageSquare, Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", perm: "Dashboard" },
  { to: "/app/daily-status", icon: ClipboardList, label: "Daily Status", perm: "Daily Status" },
  { to: "/app/receipts", icon: Receipt, label: "Receipts", perm: "Receipt" },
  { to: "/app/inquiries", icon: Inbox, label: "Website Inquiries", perm: "Website Inquiries" },
  { to: "/app/follow-up", icon: CalendarClock, label: "Follow-Up", perm: "Follow-Up" },
  { to: "/app/customers", icon: Users, label: "Customers", perm: "Customer" },
  { to: "/app/agents", icon: UserSquare2, label: "Agents", perm: "Agent" },
  { to: "/app/services", icon: Briefcase, label: "Services", perm: "Service" },
  { to: "/app/daily-assigned-service", icon: ClipboardCheck, label: "Daily Assigned Service", perm: "Daily Assigned Service" },
  { to: "/app/daily-login-service", icon: PlayCircle, label: "Daily Login Service", perm: "Daily Login Service" },
  { to: "/app/mis-report", icon: BarChart3, label: "MIS Report", perm: "MIS Report" },
  { to: "/app/service-report", icon: PieChart, label: "Service Report", perm: "Service Report" },
  { to: "/app/staff-report", icon: Users2, label: "Staff Report", perm: "Staff Report" },
  { to: "/app/reports", icon: FileBarChart2, label: "Reports", perm: "MIS Report" },
  { to: "/app/login-hours", icon: Clock3, label: "Login Hours", perm: "Login Hours" },
  { to: "/app/attendance", icon: CalendarCheck, label: "Attendance", perm: "Attendance" },
  { to: "/app/work-summary", icon: NotebookPen, label: "Work Summary", perm: "Work Summary" },
  { to: "/app/messages", icon: MessageSquare, label: "Messages" },
  { to: "/app/site-admin", icon: Globe, label: "Website Admin", admin: true },
  { to: "/app/notice", icon: Bell, label: "Notice", perm: "Notice" },
  { to: "/app/payment-types", icon: Wallet, label: "Payment Types", perm: "Payment Types" },
  { to: "/app/bank-transactions", icon: Landmark, label: "Bank Transactions", perm: "Bank Transactions" },
  { to: "/app/branches", icon: Store, label: "Branches", perm: "Branches" },
  { to: "/app/company", icon: Building2, label: "Company", perm: "Company" },
  { to: "/app/receipt-settings", icon: Settings2, label: "Receipt Settings", admin: true },
  { to: "/app/users", icon: UserCog, label: "Staff / Users", perm: "User" },
  { to: "/app/role-permissions", icon: ShieldCheck, label: "Role Permissions", perm: "Role Permission" },
];

export default function Sidebar({ onNavClick, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [counts, setCounts] = useState({ assigned_pending: 0, login_in_progress: 0, inquiries_new: 0, followup_due: 0, messages_unread: 0 });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [w, iq, fu, mu] = await Promise.allSettled([
          api.get("/counts/workload"),
          api.get("/inquiries", { params: { status: "new" } }),
          api.get("/follow-ups", { params: { status: "pending", date_to: today } }),
          api.get("/messages/unread-count"),
        ]);
        if (cancelled) return;
        const workload = w.status === "fulfilled" ? w.value.data : {};
        const inquiriesNew = iq.status === "fulfilled" ? (iq.value.data?.length || 0) : 0;
        const followupDue = fu.status === "fulfilled" ? (fu.value.data?.length || 0) : 0;
        const msgUnread = mu.status === "fulfilled" ? (mu.value.data?.count || 0) : 0;
        setCounts({ ...workload, inquiries_new: inquiriesNew, followup_due: followupDue, messages_unread: msgUnread });
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const canView = (n) => {
    if (n.admin) return isAdmin;
    if (n.managerOrAdmin) return ["admin", "manager", "branch"].includes(user?.role);
    if (isAdmin) return true;
    // Branch users get access to their own branch items; sensitive admin
    // config (Role Permissions, Company, Notice, Users, Branches) is still
    // gated by explicit permission.
    if (user?.role === "branch") {
      const ADMIN_ONLY_FOR_BRANCH = new Set(["Role Permission", "Notice", "Company", "Branches", "User"]);
      if (!n.perm) return true;
      if (!ADMIN_ONLY_FOR_BRANCH.has(n.perm)) return true;
    }
    if (!n.perm) return true;
    const p = user?.permissions?.[n.perm];
    return !!(p && p.view);
  };

  const badgeFor = (label) => {
    if (label === "Daily Assigned Service") return counts.assigned_pending;
    if (label === "Daily Login Service") return counts.login_in_progress;
    if (label === "Website Inquiries") return counts.inquiries_new;
    if (label === "Follow-Up") return counts.followup_due;
    if (label === "Messages") return counts.messages_unread;
    return 0;
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      className="w-64 shrink-0 h-screen flex flex-col text-white/85 relative"
      style={{ background: "var(--brand-navy)" }}
    >
      {children}
      <div className="px-5 py-5 border-b border-white/5 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md flex items-center justify-center font-black text-[15px]"
             style={{ background: "var(--brand-ochre)" }}>
          SS
        </div>
        <div>
          <div className="font-display font-black text-[13px] leading-tight tracking-tight">
            SHREE SAMARTH
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Consultancy CRM</div>
        </div>
      </div>

      <div className="px-4 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center font-semibold uppercase">
            {(user?.name || user?.email || "U")[0]}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || "User"}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">
              {user?.role}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {NAV.filter(canView).map((n) => {
          const b = badgeFor(n.label);
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/app"}
              onClick={onNavClick}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-[13px] hover:bg-white/5 transition-colors ${
                  isActive ? "nav-item-active text-white" : "text-white/75"
                }`
              }
            >
              <n.icon className="h-4 w-4 shrink-0" />
              <span className="truncate flex-1">{n.label}</span>
              {b > 0 && (
                <span
                  data-testid={`nav-badge-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-black font-mono-num ${
                    n.label === "Daily Assigned Service"
                      ? "bg-[color:var(--brand-ochre)] text-white"
                      : n.label === "Website Inquiries"
                      ? "bg-red-500 text-white animate-pulse"
                      : n.label === "Follow-Up"
                      ? "bg-amber-500 text-white"
                      : "bg-blue-500 text-white"
                  }`}
                >
                  {b > 99 ? "99+" : b}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <button
        data-testid="logout-btn"
        onClick={handleLogout}
        className="m-3 flex items-center justify-center gap-2 py-2.5 rounded-md bg-white/5 hover:bg-white/10 text-sm transition-colors"
      >
        <LogOut className="h-4 w-4" /> Logout
      </button>
    </aside>
  );
}
